/**
 * Mensajería v2 — helpers de presentación.
 *
 * Acá vive todo lo que convierte un dato del backend en algo que se pueda
 * mirar: duraciones, iniciales, el nivel de riesgo y las etiquetas de origen.
 * No hay llamadas a la API ni estado; se puede usar desde cualquier
 * componente de la v2 sin arrastrar nada.
 */

/** Iniciales para los avatares. "Valentina Ríos" → "VR" */
export function ini (name) {
  if (!name) return '?'
  return String(name).trim().split(/\s+/).slice(0, 2)
    .map(w => w.charAt(0)).join('').toUpperCase()
}

/** Segundos → "3d 4h" / "5h 18m" / "22m" / "45s". null → "—" */
export function dur (secs) {
  if (secs == null || isNaN(secs)) return '—'
  const s = Math.max(0, Math.floor(secs))
  if (s < 60) return s + 's'
  const d = Math.floor(s / 86400)
  const h = Math.floor((s % 86400) / 3600)
  const m = Math.floor((s % 3600) / 60)
  if (d > 0) return `${d}d ${h}h`
  if (h > 0) return `${h}h ${m}m`
  return `${m}m`
}

/** Segundos transcurridos desde una fecha ISO. null si no hay fecha. */
export function since (iso) {
  if (!iso) return null
  const t = new Date(iso).getTime()
  if (isNaN(t)) return null
  return Math.max(0, Math.floor((Date.now() - t) / 1000))
}

/**
 * Hace cuánto que el cliente escribió y nadie contestó, en segundos.
 *
 * El backend todavía no manda `awaiting_reply_seconds` (ver PLAN-INTEGRACION).
 * Mientras no exista, se cae a `last_client_message_at`, que no es lo mismo
 * —no sabe si le contestamos después— pero deja el orden de la cola razonable.
 * Cuando el campo llegue, esto empieza a ser exacto sin tocar nada más.
 */
export function waitingSecs (conv) {
  if (!conv) return null
  if (conv.awaiting_reply_seconds != null) return conv.awaiting_reply_seconds
  return since(conv.last_client_message_at)
}

/** Segundos que lleva la conversación quieta en su etapa. */
export function stageSecs (conv) {
  if (!conv) return null
  return since(conv.last_moved_at || conv.created_at)
}

/**
 * El único dato que necesita color: cuánto duele.
 *
 *   risk  · 24 h sin responder, o 72 h sin moverse
 *   warn  ·  4 h sin responder, o 48 h sin moverse
 *   ok    · dentro de lo esperado
 *   done  · etapa final, ya no corre el reloj
 */
export function riskOf (conv, stageIsFinal) {
  if (stageIsFinal) return 'done'
  const w = waitingSecs(conv) || 0
  const s = stageSecs(conv) || 0
  if (w >= 86400 || s >= 259200) return 'risk'
  if (w >= 14400 || s >= 172800) return 'warn'
  return 'ok'
}

/** Etiqueta de origen. Para Meta se muestra el código de campaña, que es el dato útil. */
export function originLabel (conv) {
  if (!conv || !conv.lead_origin) return ''
  if (conv.lead_origin === 'meta') return String(conv.lead_ads_code || '').trim() || 'Meta'
  if (conv.lead_origin === 'erasmusplay') return 'ErasmusPlay'
  if (conv.lead_origin === 'directo') return 'Directo'
  return ''
}

/** Clase del punto de color del origen. */
export function originDot (conv) {
  const o = conv && conv.lead_origin
  return o === 'erasmusplay' ? 'ep' : o === 'meta' ? 'meta' : 'web'
}

/** Teléfono legible, sacando el sufijo de WhatsApp si viene pegado. */
export function convPhone (conv) {
  if (!conv) return ''
  const raw = conv.telefono || conv.client_identifier || ''
  return String(raw).split('@')[0]
}

/**
 * ¿El número está completo? Un número corto no se puede escribir por WhatsApp,
 * y eso hoy se pierde hasta que alguien intenta contestar.
 */
export function phoneComplete (conv) {
  const digits = convPhone(conv).replace(/[^0-9]/g, '')
  return digits.length >= 10
}

/** Normaliza búsquedas: sin acentos y en minúscula. */
export function norm (str) {
  return str ? String(str).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '') : ''
}

/** ¿La conversación casa con lo buscado? Nombre, teléfono o intake. */
export function matches (conv, q) {
  const needle = norm(q).trim()
  if (!needle) return true
  const hay = norm([conv.client_name, convPhone(conv), conv.intake_id, conv.id].join(' '))
  return hay.indexOf(needle) >= 0
}

/* ─── Filtro por rango de fechas (last_move_seccion) ──────────────
   La lógica viene del dashboard anterior (KanbanBoard.vue, ya eliminado): el rango se
   escribe en fechas LOCALES 'YYYY-MM-DD', las dos inclusive, y filtra por
   `last_move_seccion` —el último movimiento de sección del chat—, que es
   lo que el backend entiende como ?desde / ?hasta.

   Vive acá porque ahora el rango lo elige la barra superior y lo tienen
   que leer tres lugares (la cola del panel, el tablero y la lista del
   chat). Con una sola copia no se pueden ir separando. */

/** Fecha local (la del navegador, que en la oficina es hora de Chile) → 'YYYY-MM-DD'. */
export function localDate (date) {
  const d = new Date(date)
  const mes = String(d.getMonth() + 1).padStart(2, '0')
  const dia = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${mes}-${dia}`
}

/** 'YYYY-MM-DD' → 'dd/mm', para el chip de la barra. */
export function shortDate (iso) {
  const [, mes, dia] = String(iso).split('-')
  return (dia && mes) ? `${dia}/${mes}` : iso
}

/** Los atajos del menú, en el orden en que se muestran. */
export const DATE_PRESETS = [
  { key: 'hoy',    label: 'Hoy' },
  { key: 'ayer',   label: 'Ayer' },
  { key: 'semana', label: 'Últimos 7 días' },
  { key: 'mes',    label: 'Este mes' }
]

/** Rango { desde, hasta } de cada atajo. */
export function presetRange (key) {
  const hoy = new Date()
  switch (key) {
    case 'hoy': {
      const d = localDate(hoy)
      return { desde: d, hasta: d }
    }
    case 'ayer': {
      const ayer = new Date(hoy)
      ayer.setDate(ayer.getDate() - 1)
      const d = localDate(ayer)
      return { desde: d, hasta: d }
    }
    case 'semana': {
      const inicio = new Date(hoy)
      // Hoy incluido: hoy y los 6 días anteriores.
      inicio.setDate(inicio.getDate() - 6)
      return { desde: localDate(inicio), hasta: localDate(hoy) }
    }
    case 'mes': {
      const inicio = new Date(hoy.getFullYear(), hoy.getMonth(), 1)
      return { desde: localDate(inicio), hasta: localDate(hoy) }
    }
    default:
      return { desde: '', hasta: '' }
  }
}

/**
 * ¿La conversación cae adentro del rango? Es el mismo criterio que aplica el
 * backend en el tablero (applyDateRange): rango inclusive sobre
 * `last_move_seccion` y, con el filtro activo, afuera las que nunca se
 * movieron de sección (last_move_seccion en NULL).
 *
 * Se usa donde el filtro NO lo puede hacer el servidor: hoy solo el endpoint
 * del tablero entiende ?desde/?hasta, así que la cola del panel y la lista
 * del chat lo aplican acá sobre lo que ya tienen cargado.
 */
export function inDateRange (conv, desde, hasta) {
  if (!desde && !hasta) return true
  const iso = conv && conv.last_move_seccion
  if (!iso) return false
  const t = new Date(iso)
  if (isNaN(t.getTime())) return false
  // Las dos puntas son fechas locales sin hora: comparar 'YYYY-MM-DD' como
  // texto alcanza y evita pelearse con zonas horarias.
  const dia = localDate(t)
  if (desde && dia < desde) return false
  if (hasta && dia > hasta) return false
  return true
}

/* ─── Links en los mensajes ───────────────────────────────────────
   El texto del chat lo escribe gente de afuera: son mensajes de WhatsApp de
   leads. Cualquier cosa que se pinte con v-html tiene que pasar SÍ O SÍ por
   escapeHtml primero, o un mensaje con `<img onerror=...>` corre código en el
   backoffice. Por eso las dos funciones viven juntas y linkify() escapa antes
   de tocar nada: no hay forma de usar una sin la otra. */

const HTML_ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }

/** Deja el texto inofensivo para meterlo en HTML. */
export function escapeHtml (str) {
  return String(str == null ? '' : str).replace(/[&<>"']/g, c => HTML_ESCAPES[c])
}

/* Solo http, https y www: `javascript:` y `data:` quedan afuera a propósito,
   así un mensaje no puede fabricar un link que ejecute algo al tocarlo. */
const URL_RE = /\b(?:https?:\/\/|www\.)[^\s<]+/gi

/**
 * Texto plano → HTML seguro con los links navegables.
 *
 * Devuelve HTML, así que solo se puede usar con v-html.
 */
export function linkify (text) {
  return escapeHtml(text).replace(URL_RE, (crudo) => {
    let url = crudo
    let cola = ''
    /* La puntuación pegada al final es de la frase, no del link: nadie escribe
       una URL que termine en punto, y en "(www.houseandflats.com)" el
       paréntesis cierra la aclaración. El ) solo se devuelve si no hay uno
       abierto adentro de la propia URL. */
    for (;;) {
      const ultimo = url.slice(-1)
      if ('.,;:!?'.indexOf(ultimo) >= 0 && ultimo !== '') {
        cola = ultimo + cola; url = url.slice(0, -1); continue
      }
      if (ultimo === ')' && (url.match(/\(/g) || []).length < (url.match(/\)/g) || []).length) {
        cola = ultimo + cola; url = url.slice(0, -1); continue
      }
      break
    }
    if (!url) return crudo
    // Sin esquema el navegador lo tomaría como ruta relativa del backoffice.
    const href = /^www\./i.test(url) ? 'https://' + url : url
    return `<a class="mx-lnk" href="${href}" target="_blank" rel="noopener noreferrer">${url}</a>${cola}`
  })
}

/** Cabeceras con el token de la sesión, iguales a las del dashboard actual. */
export function authHeaders (extra) {
  return Object.assign({
    'Accept': 'application/json',
    'Authorization': localStorage.getItem('token') || ''
  }, extra || {})
}

/** Bytes → "2.3 MB". Para los documentos del chat. */
export function bytes (n) {
  if (n == null) return ''
  if (n < 1024) return n + ' B'
  if (n < 1048576) return Math.round(n / 1024) + ' KB'
  return (n / 1048576).toFixed(1).replace('.0', '') + ' MB'
}

/**
 * Tonos de las columnas del tablero. El color identifica la etapa sin leer el
 * encabezado. Se reparte por posición, así una columna nueva nace con el suyo
 * y no todas grises.
 */
/* Dos intensidades del mismo tono: `soft` para el lavado de arriba de la
   columna y `line` para el borde de la cabecera y la píldora del contador.
   Son los valores de la maqueta, que es la referencia de diseño. Probamos
   subirlos (tapa de color, cabecera y cuerpo teñidos, borde de columna) y
   quedaba demasiado: el tablero se leía como cinco bloques de color en vez de
   como cinco listas. */
const HUES = [
  { h: '#5B6B87', soft: 'rgba(91,107,135,.09)',  line: 'rgba(91,107,135,.22)' },
  { h: '#0E8FA6', soft: 'rgba(14,143,166,.09)',  line: 'rgba(14,143,166,.22)' },
  { h: '#B4791A', soft: 'rgba(180,121,26,.10)',  line: 'rgba(180,121,26,.24)' },
  { h: '#7C5CD6', soft: 'rgba(124,92,214,.09)',  line: 'rgba(124,92,214,.22)' },
  { h: '#2C6FD6', soft: 'rgba(44,111,214,.09)',  line: 'rgba(44,111,214,.22)' }
]
const HUE_WIN  = { h: '#12946A', soft: 'rgba(18,148,106,.09)', line: 'rgba(18,148,106,.22)' }
const HUE_LOSE = { h: '#C4453F', soft: 'rgba(196,69,63,.09)',  line: 'rgba(196,69,63,.22)' }

/** Ganado siempre verde y perdido siempre rojo; el resto por posición. */
export function stageHue (stage, index) {
  const key = String((stage && (stage.key || stage.slug)) || '')
  if (/gana|exito|alquiler/i.test(key)) return HUE_WIN
  if (/perd|fallid|intake_fallido/i.test(key)) return HUE_LOSE
  return HUES[index % HUES.length]
}
